import { monthsBetween, type Day } from '../core/calendar.ts';
import { toDouble, type Decimal } from '../core/decimal.ts';
import type { Goal } from '../types/domain.ts';
import { clampPercent } from './percent.ts';

/** GoalDateMatch: when (if ever) a goal is reached in a series, and the series' first value. */
export interface GoalDateMatch {
  readonly goal: Goal;
  readonly date: Day | null;
  readonly currentValue: Decimal;
}

/**
 * GoalDateMatch.progress, in Double, relative to the goal's startingValue. Growth goals use
 * (current − start)/(target − start), reduction goals (start − current)/(start − target),
 * clamped to 0…100; a non-positive denominator gives 100 if already met, else 0.
 */
export function goalDateMatchProgress(goal: Goal, currentValue: Decimal): number {
  const current = toDouble(currentValue);
  const target = toDouble(goal.targetValue);
  const start = toDouble(goal.startingValue);
  if (goal.operation === 'crossunder') {
    const totalNeeded = start - target;
    if (!(totalNeeded > 0)) return current <= target ? 100 : 0;
    return clampPercent(((start - current) / totalNeeded) * 100);
  }
  const totalNeeded = target - start;
  if (!(totalNeeded > 0)) return current >= target ? 100 : 0;
  return clampPercent(((current - start) / totalNeeded) * 100);
}

/** GoalDateMatch.isAchieved: a match date exists. */
export const isMatchAchieved = (match: Pick<GoalDateMatch, 'date'>): boolean => match.date !== null;

/**
 * estimatedMonthsRemaining(now:): null without a date, 0 when the date is not after `now`,
 * else whole calendar months from `now` to the date.
 */
export function monthsUntilMatch(date: Day | null, now: Day): number | null {
  if (date === null) return null;
  if (!(date > now)) return 0;
  return monthsBetween(now, date);
}
